import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Which release versions have been read.
//
// D1 / R1 — READ THIS BEFORE RENAMING ANYTHING HERE. This is scoped to a
// BROWSER, not to a person: one global localStorage key, no user id in it. Two
// people sharing a browser share one read state, and the same person on a
// laptop and a phone has two. That was chosen deliberately over Clerk
// unsafeMetadata and a backend collection; it is a known trade-off, not an
// oversight. Every identifier here says `read state`, never `user's read
// state`, so the code cannot quietly claim a guarantee the storage does not
// provide.
export const WHATS_NEW_READ_KEY = 'cg.whatsNew.read';

// zustand's `persist` guards reading (it try/catches `getStorage()` and
// hydration) but NOT writing: `setItem` calls straight through to
// `storage.setItem` with no catch. `markRead` and `seed` are both called from
// mount effects in the app shell, so a storage that exists but rejects writes —
// an exhausted quota, or a browser configured to block site data — would throw
// out of an effect, hit the nearest error boundary, and unmount the shell.
//
// A cosmetic release-notes feature must never be able to take the app down.
// Losing the write is the correct failure: read state stays in memory for the
// session and is simply not remembered.
const safeLocalStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      /* quota exhausted or storage blocked — read state is in-memory only */
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      /* nothing to do */
    }
  },
};

export const useWhatsNewReadStore = create(
  persist(
    (set, get) => ({
      readVersions: [],
      // Distinguishes "never seeded" from "seeded, and everything happens to be
      // read". Without it a returning user whose read state legitimately covers
      // every release is indistinguishable from a first-time visitor, and the
      // back-fill would run again on a list it has already judged.
      seeded: false,

      // Union, never replace. A client running an older build sees a shorter
      // release list; replacing would silently mark unseen newer versions read.
      // A Set also makes repeated marking idempotent, so the array cannot grow
      // without bound as the same versions are re-marked on every panel open.
      markRead: (versions) => {
        if (!Array.isArray(versions) || versions.length === 0) return;
        const next = new Set(get().readVersions);
        versions.forEach((v) => {
          if (typeof v === 'string' && v) next.add(v);
        });
        set({ readVersions: [...next] });
      },

      // The first-run back-fill (see seedReadState). Runs exactly once, ever.
      seed: (versions) => {
        if (get().seeded) return;
        const next = new Set(get().readVersions);
        (Array.isArray(versions) ? versions : []).forEach((v) => {
          if (typeof v === 'string' && v) next.add(v);
        });
        set({ readVersions: [...next], seeded: true });
      },
    }),
    {
      name: WHATS_NEW_READ_KEY,
      version: 1,
      storage: createJSONStorage(() => safeLocalStorage),
    },
  ),
);

export default useWhatsNewReadStore;
